import { RecurringTypesEnum, SubscriptionProductNamesEnum } from "dash-common";
import {
  DropdownOptions,
  Flex,
  localStorage,
  Spinner,
  Text,
  TooltipPopup,
  ViewerContext,
} from "dash-ui";
import CompleteSignUpPageLayout from "flow-components/CompleteSignUpPageLayout/CompleteSignUpPageLayout";
import { RoutesPath } from "flow-components/routes/routes";
import {
  SubscriptionProduct,
  useCreateCheckoutSessionMutation,
  useGetAppBasicInfoQuery,
  useGetSubscriptionProductsQuery,
} from "gql/generated.graphql";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useTheme } from "styled-components";

import { getProductNameSlug } from "common/helpers";
import { RecurringTypeToggle } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionProductsSection/components";
import SubscriptionProductCard from "v2/components/billing/SubscriptionProductCard/SubscriptionProductCard";
import { HeaderContainer } from "./PickSubscriptionPage.styles";

function PickSubscriptionPage() {
  useEffect(() => {
    const isApiFirstAccess = localStorage.getApiFirstAccess();
    if (isApiFirstAccess) {
      localStorage.setApiFirstAccess("");
    }
  }, []);
  const { t } = useTranslation(["pages", "common"]);
  const theme = useTheme();
  const [recurringType, setRecurringType] = useState(RecurringTypesEnum.MONTH);
  const { subscription } = useContext(ViewerContext);

  const { data: pluginUserData, loading: pluginUserQueryLoading } =
    useGetAppBasicInfoQuery();

  const isApiUser = !!pluginUserData?.getApiCredentialsBasicInfo;

  const { data, loading } = useGetSubscriptionProductsQuery({
    skip: pluginUserQueryLoading,
    variables: {
      filter: {
        isApiUser,
      },
    },
  });

  const [createCheckoutSession, { loading: checkoutLoading }] =
    useCreateCheckoutSessionMutation();

  const products = data?.getSubscriptionProducts || [];

  const handleToggleRecurringType = (newRecurringType: RecurringTypesEnum) => {
    setRecurringType(newRecurringType);
  };

  const handleProductSelect = async (product: SubscriptionProduct) => {
    const productSlug = getProductNameSlug(product);

    const data = await createCheckoutSession({
      variables: {
        input: {
          priceId: product.stripePriceId,
          productId: product.stripeProductId,
          successUrl: `${window.location.origin}/auth/stripe-checkout-result?subscription=${productSlug}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
        },
      },
    });

    const sessionUrl = data.data?.createCheckoutSession.sessionUrl;

    return window.open(sessionUrl, "_self");
  };

  if (subscription && !isApiUser) {
    return <Navigate to={RoutesPath.Dashboard} />;
  }

  return (
    <CompleteSignUpPageLayout activeStep={2}>
      <DropdownOptions />
      <TooltipPopup />
      <Flex w="100%" flexDirection="column">
        <HeaderContainer w="100%" justifyContent="space-between">
          <Flex flexDirection="column">
            <Text bold xxl>
              {t("pages:completeSignUpPickSubscription:header")}
            </Text>
            <Text color={theme.colors.n80}>
              {t("pages:completeSignUpPickSubscription:subheader")}
            </Text>
          </Flex>
        </HeaderContainer>
        {loading || pluginUserQueryLoading ? (
          <Flex w="100%" alignItems="center" justifyContent="center">
            <Spinner />
          </Flex>
        ) : (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            w="100%"
            flexWrap="wrap"
          >
            <Flex alignItems="center" gap={10} my={4}>
              {!isApiUser && (
                <RecurringTypeToggle
                  recurringType={recurringType}
                  onToggle={handleToggleRecurringType}
                  className="mt-2"
                />
              )}
            </Flex>
            <div className="w-full flex flex-col gap-4 flex-grow justify-center items-center xl:flex-row xl:items-stretch">
              {products
                .filter(
                  (product) =>
                    (isApiUser
                      ? product.name === SubscriptionProductNamesEnum.API
                      : product.name !== SubscriptionProductNamesEnum.API) &&
                    product.recurringType === recurringType
                )
                .map((product) => (
                  <SubscriptionProductCard
                    key={product._id}
                    product={product as SubscriptionProduct}
                    onSelectPlan={handleProductSelect}
                    loading={checkoutLoading}
                    animated
                    isCurrent={false}
                  />
                ))}
            </div>
          </Flex>
        )}
      </Flex>
    </CompleteSignUpPageLayout>
  );
}

export default PickSubscriptionPage;
